import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthResponse, AuthUser, Permission, UserRole } from '@manpower/shared';
import { ApiRequestError, api } from '@/lib/api';

interface AuthContextValue {
  user: AuthUser | null;
  /** True only while the very first /auth/me is in flight. */
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  hasPermission: (...permissions: Permission[]) => boolean;
  signIn: (result: AuthResponse) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    // Tokens live in httpOnly cookies, so "am I signed in?" can only be
    // answered by the server. A 401 here is the normal anonymous case.
    queryFn: async () => {
      try {
        return await api.get<AuthUser>('/auth/me');
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const user = data ?? null;

  const signOutMutation = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    // Clear the cache either way — if the call failed the cookies may still be
    // gone, and leaving stale private data on screen is the worse outcome.
    onSettled: () => queryClient.clear(),
  });

  const signIn = useCallback(
    (result: AuthResponse) => {
      queryClient.setQueryData(['auth', 'me'], result.user);
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      hasRole: (...roles) => (user ? roles.includes(user.role) : false),
      // SUPER_ADMIN holds every permission; the API materialises the full list
      // onto the user, so a plain includes() is enough here.
      hasPermission: (...permissions) =>
        user ? permissions.every((p) => user.permissions.includes(p)) : false,
      signIn,
      signOut,
      refresh,
    }),
    [user, isLoading, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
